<template>
  <div class="trivia-wizard">
    <div class="trivia-wizard__header">
      <h1>{{ isEditMode ? $t('trivia.create.title.edit') : isViewMode ? $t('trivia.create.title.view') : $t('trivia.create.title.new') }}</h1>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" @click="$router.push({ name: 'trivia' })">
        <v-icon left>mdi-arrow-left</v-icon>
        {{ $t('trivia.create.button.back') }}
      </v-btn>
    </div>

    <div v-if="!isViewMode" class="trivia-wizard__steps">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="trivia-wizard__step"
        :class="{
          'trivia-wizard__step--active': currentStep === index,
          'trivia-wizard__step--completed': currentStep > index
        }"
      >
        <div class="trivia-wizard__step-number">{{ index + 1 }}</div>
        <span class="trivia-wizard__step-label">{{ step.label }}</span>
      </div>
    </div>

    <div class="trivia-wizard__content">
      <div v-if="currentStep === 0">
        <v-card flat class="tw-card">
          <div class="tw-card__heading"><span>{{ $t('trivia.create.section.general') }}</span></div>
          <v-text-field
            v-model="triviaForm.title"
            :label="$t('trivia.create.label.title')"
            outlined
            dense
            :readonly="isViewMode"
            :rules="rules.title"
            validate-on-blur
          ></v-text-field>

          <v-textarea
            v-model="triviaForm.description"
            :label="$t('trivia.create.label.description')"
            outlined
            dense
            rows="3"
            :readonly="isViewMode"
          ></v-textarea>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="triviaForm.defaultTimeLimitSeconds"
                :label="$t('trivia.create.label.timeLimit')"
                type="number"
                outlined
                dense
                :readonly="isViewMode"
                :rules="rules.timeLimit"
                validate-on-blur
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="triviaForm.defaultBasePoints"
                :label="$t('trivia.create.label.basePoints')"
                type="number"
                outlined
                dense
                :readonly="isViewMode"
                :rules="rules.basePoints"
                validate-on-blur
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="triviaForm.resultsDisplaySeconds"
                :label="$t('trivia.create.label.resultsDisplay')"
                type="number"
                outlined
                dense
                :readonly="isViewMode"
                :rules="rules.resultsDisplay"
                validate-on-blur
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-switch
                v-model="triviaForm.speedBonusEnabled"
                :label="$t('trivia.create.label.speedBonus')"
                :readonly="isViewMode"
                inset
              ></v-switch>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6" class="d-flex align-center">
              <v-switch
                v-model="triviaForm.autoAdvanceEnabled"
                :label="$t('trivia.create.label.autoAdvance')"
                :readonly="isViewMode"
                inset
              ></v-switch>
            </v-col>
          </v-row>
        </v-card>
      </div>

      <div v-if="currentStep === 1">
        <v-card flat class="tw-card">
          <div class="tw-card__heading">
            <span>{{ questionFormTitle }}</span>
            <v-chip v-if="isEditingQuestion" small color="amber" text-color="white">{{ $t('trivia.create.chip.editing') }}</v-chip>
          </div>
          <v-text-field
            v-model="newQuestion.title"
            :label="$t('trivia.create.label.question')"
            outlined
            dense
            :readonly="isViewMode"
            :rules="rules.questionTitle"
            counter="255"
            maxlength="255"
            validate-on-blur
          ></v-text-field>

          <v-select
            v-model="newQuestion.type"
            :items="questionTypes"
            :label="$t('trivia.create.label.type')"
            outlined
            dense
            :readonly="isViewMode || isEditingQuestion"
            item-text="label"
            item-value="value"
            :rules="rules.questionType"
            validate-on-blur
          ></v-select>

          <div v-if="newQuestion.type === 'SINGLE_CHOICE'" class="tw-options">
            <div class="tw-section-label">{{ $t('trivia.create.section.options') }}</div>
            <v-radio-group
              v-model="newQuestion.correctIndex"
              :error-messages="radioErrorMessages"
              @change="radioTouched = true"
              hide-details="auto"
              class="tw-options__group"
            >
              <div
                v-for="(opt, idx) in newQuestion.options"
                :key="idx"
                class="tw-option-row"
                :class="{ 'tw-option-row--correct': newQuestion.correctIndex === idx }"
              >
                <div class="tw-option-row__badge">{{ optionLetter(idx) }}</div>
                <v-text-field
                  v-model="opt.label"
                  :placeholder="$t('trivia.create.option.placeholder', { number: idx + 1 })"
                  solo
                  flat
                  hide-details="auto"
                  :readonly="isViewMode"
                  :rules="rules.optionLabel"
                  counter="255"
                  maxlength="255"
                  class="tw-option-row__input"
                ></v-text-field>
                <v-radio
                  :value="idx"
                  :disabled="isViewMode"
                  hide-details
                  class="tw-option-row__radio"
                  :label="newQuestion.correctIndex === idx ? $t('trivia.create.option.correct') : $t('trivia.create.option.mark')"
                ></v-radio>
                <v-btn icon @click="removeNewOption(idx)" :disabled="isViewMode" class="tw-option-row__delete">
                  <v-icon color="red lighten-1">mdi-close-circle</v-icon>
                </v-btn>
              </div>
            </v-radio-group>
            <v-btn
              text
              color="primary"
              @click="addNewOption"
              class="tw-add-option"
              :disabled="isViewMode || newQuestion.options.length >= 4"
            >
              <v-icon left>mdi-plus-circle-outline</v-icon>
              {{ $t('trivia.create.button.addOption') }}
              <span v-if="newQuestion.options.length >= 2" class="tw-add-option__limit">{{ $t('trivia.create.option.limit', { max: 4 }) }}</span>
            </v-btn>
          </div>

          <div v-if="newQuestion.type === 'TRUE_FALSE'" class="mb-4">
            <v-select
              v-model="newQuestion.correctAnswer"
              :items="[{ label: $t('trivia.create.answer.true'), value: true }, { label: $t('trivia.create.answer.false'), value: false }]"
              :label="$t('trivia.create.label.correctAnswer')"
              outlined
              dense
              item-text="label"
              item-value="value"
              :readonly="isViewMode"
            ></v-select>
          </div>

          <div class="tw-question-actions">
            <v-btn color="primary" depressed @click="addQuestion" :disabled="isViewMode || !isNewQuestionValid">
              <v-icon left>{{ isEditingQuestion ? 'mdi-content-save' : 'mdi-plus' }}</v-icon> {{ questionButtonLabel }}
            </v-btn>
            <v-btn v-if="isEditingQuestion" text color="grey darken-1" @click="cancelEditQuestion">
              {{ $t('trivia.create.button.cancel') }}
            </v-btn>
          </div>
          <v-alert v-if="questionsLimitReached" type="info" dense text class="mt-3 mb-0">
            {{ $t('trivia.create.question.limitReached', { max: maxQuestions }) }}
          </v-alert>
        </v-card>

        <v-card flat class="tw-card mt-6">
          <div class="tw-card__heading">
            <span>{{ $t('trivia.create.section.questions') }}</span>
            <v-chip small class="tw-count-chip">{{ triviaForm.questions.length }} / {{ maxQuestions }}</v-chip>
          </div>

          <div v-if="triviaForm.questions.length > 0" class="tw-question-list">
            <div v-for="(q, i) in triviaForm.questions" :key="i" class="tw-question-item">
              <div class="tw-question-item__index">{{ i + 1 }}</div>
              <div class="tw-question-item__body">
                <div class="tw-question-item__title">{{ q.title }}</div>
                <div class="tw-question-item__meta">
                  <v-chip x-small class="tw-type-chip">
                    {{ $t('trivia.create.questionType.' + q.type) }}
                  </v-chip>
                  <span class="tw-question-item__correct">
                    <v-icon x-small color="green">mdi-check-circle</v-icon>
                    {{ q.type === 'SINGLE_CHOICE' ? (q.options.find(o => o.isCorrect)?.label || '—') : (q.correctAnswer ? $t('trivia.create.answer.true') : $t('trivia.create.answer.false')) }}
                  </span>
                </div>
              </div>
              <div class="tw-question-item__actions">
                <v-btn icon @click="editQuestion(i)" :disabled="isViewMode" :title="$t('trivia.list.action.edit')">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon @click="removeQuestion(i)" :disabled="isViewMode" :title="$t('trivia.list.action.delete')">
                  <v-icon color="red lighten-1">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div v-else class="tw-empty">
            <v-icon size="48" color="grey lighten-1">mdi-help-circle-outline</v-icon>
            <p>{{ $t('trivia.create.question.empty') }}</p>
          </div>
        </v-card>
      </div>

      <div v-if="currentStep === 2">
        <v-card flat class="tw-card">
          <div class="tw-preview-head">
            <h2>{{ triviaForm.title || $t('trivia.create.preview.noTitle') }}</h2>
            <p>{{ triviaForm.description || $t('trivia.create.preview.noDescription') }}</p>
          </div>

          <div class="tw-stats">
            <div class="tw-stat">
              <v-icon color="deep-purple">mdi-timer-outline</v-icon>
              <span class="tw-stat__value">{{ triviaForm.defaultTimeLimitSeconds }}s</span>
              <span class="tw-stat__label">{{ $t('trivia.create.preview.stat.time') }}</span>
            </div>
            <div class="tw-stat">
              <v-icon color="amber darken-2">mdi-star-outline</v-icon>
              <span class="tw-stat__value">{{ triviaForm.defaultBasePoints }}</span>
              <span class="tw-stat__label">{{ $t('trivia.create.preview.stat.basePoints') }}</span>
            </div>
            <div class="tw-stat">
              <v-icon color="teal">mdi-flash-outline</v-icon>
              <span class="tw-stat__value">{{ triviaForm.speedBonusEnabled ? $t('trivia.common.yes') : $t('trivia.common.no') }}</span>
              <span class="tw-stat__label">{{ $t('trivia.create.preview.stat.speedBonus') }}</span>
            </div>
            <div class="tw-stat">
              <v-icon color="indigo">mdi-play-speed</v-icon>
              <span class="tw-stat__value">{{ triviaForm.autoAdvanceEnabled ? $t('trivia.common.yes') : $t('trivia.common.no') }}</span>
              <span class="tw-stat__label">{{ $t('trivia.create.preview.stat.autoAdvance') }}</span>
            </div>
          </div>

          <div class="tw-card__heading mt-6">
            <span>{{ $t('trivia.create.preview.section.questions') }}</span>
            <v-chip small class="tw-count-chip">{{ triviaForm.questions.length }}</v-chip>
          </div>
          <v-expansion-panels v-if="triviaForm.questions.length > 0" flat>
            <v-expansion-panel v-for="(q, i) in triviaForm.questions" :key="i">
              <v-expansion-panel-header>{{ i + 1 }}. {{ q.title }}</v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="text-subtitle-2">{{ $t('trivia.create.preview.type') }} {{ $t('trivia.create.questionType.' + q.type) }}</p>
                <v-list dense v-if="q.type === 'SINGLE_CHOICE'">
                  <v-list-item v-for="(opt, j) in q.options" :key="j">
                    <v-list-item-content>
                      <v-list-item-title :class="{ 'green--text': opt.isCorrect }">{{ opt.label }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <p v-else class="mt-2">{{ $t('trivia.create.preview.correctAnswer') }}<strong>{{ q.correctAnswer ? $t('trivia.create.answer.true') : $t('trivia.create.answer.false') }}</strong></p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-alert v-else type="warning" text dense>{{ $t('trivia.create.preview.emptyQuestions') }}</v-alert>
        </v-card>
      </div>
    </div>

    <div v-if="!isViewMode" class="trivia-wizard__footer">
      <v-btn
        v-if="currentStep > 0"
        outlined
        color="primary"
        @click="previousStep"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        {{ $t('trivia.create.button.backFooter') }}
      </v-btn>
      <v-spacer></v-spacer>

      <v-btn
        v-if="currentStep < steps.length - 1"
        color="primary"
        @click="nextStep"
        :disabled="!canAdvance || saving"
      >
        {{ $t('trivia.create.button.next') }}
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>

      <template v-if="currentStep === steps.length - 1">
        <v-btn outlined color="primary" @click="saveTrivia" :disabled="!canSave || saving" class="mr-2">
          {{ $t('trivia.create.button.saveDraft') }}
        </v-btn>
        <v-btn color="success" @click="publishTrivia" :disabled="!canSave || saving">
          {{ $t('trivia.create.button.publish') }}
        </v-btn>
      </template>
    </div>
  </div>
</template>

<script>
import { service as TriviaService } from '@/services/trivia_service.js';
import i18n from '@/plugins/i18n';
import Swal from 'sweetalert2';

const MAX_QUESTIONS = 20;

function getSessionContext() {
    return {
        idGroup: sessionStorage.getItem('idGroup'),
        idEmpresa: sessionStorage.getItem('idEmpresa'),
        idUsuario: sessionStorage.getItem('idUsuario')
    };
}

function normalizeTriviaForBackend(form) {
    return {
        title: form.title,
        description: form.description || '',
        defaultTimeLimitSeconds: form.defaultTimeLimitSeconds,
        defaultBasePoints: form.defaultBasePoints,
        speedBonusEnabled: form.speedBonusEnabled,
        resultsDisplaySeconds: form.resultsDisplaySeconds,
        autoAdvanceEnabled: form.autoAdvanceEnabled
    };
}

function normalizeQuestionForBackend(question, defaultBasePoints, defaultTimeLimitSeconds) {
    const payload = {
        type: question.type,
        title: question.title,
        basePoints: question.basePoints || defaultBasePoints,
        timeLimitSeconds: question.timeLimitSeconds || defaultTimeLimitSeconds
    };

    if (question.type === 'SINGLE_CHOICE') {
        payload.options = question.options.map(o => ({
            ...(o.optionId ? { optionId: o.optionId } : {}),
            label: o.label,
            isCorrect: !!o.isCorrect
        }));
    } else if (question.type === 'TRUE_FALSE') {
        payload.correctAnswer = question.correctAnswer;
    }

    return payload;
}

export default {
  name: "createTriviaComponent",

  data() {
    return {
      currentStep: 0,
      loading: false,
      saving: false,
      steps: [
        { label: i18n.t('trivia.create.step.general') },
        { label: i18n.t('trivia.create.step.questions') },
        { label: i18n.t('trivia.create.step.preview') }
      ],
      questionTypes: [
        { label: i18n.t('trivia.create.questionType.singleChoice'), value: 'SINGLE_CHOICE' },
        { label: i18n.t('trivia.create.questionType.trueFalse'), value: 'TRUE_FALSE' }
      ],
      rules: {
        title: [v => !!v || i18n.t('trivia.create.validation.title.required')],
        questionTitle: [
          v => !!v || i18n.t('trivia.create.validation.question.required'),
          v => !v || v.length <= 255 || i18n.t('trivia.create.validation.question.maxLength')
        ],
        optionLabel: [
          v => !v || v.length <= 255 || i18n.t('trivia.create.validation.option.maxLength')
        ],
        questionType: [v => !!v || i18n.t('trivia.create.validation.type.required')],
        timeLimit: [
          v => (v !== '' && v !== null && v !== undefined && !isNaN(Number(v))) || i18n.t('trivia.create.validation.timeLimit.required'),
          v => Number(v) > 0 || i18n.t('trivia.create.validation.timeLimit.greaterThan')
        ],
        basePoints: [
          v => (v !== '' && v !== null && v !== undefined && !isNaN(Number(v))) || i18n.t('trivia.create.validation.basePoints.required'),
          v => Number(v) > 0 || i18n.t('trivia.create.validation.basePoints.greaterThan')
        ],
        resultsDisplay: [
          v => (v !== '' && v !== null && v !== undefined && !isNaN(Number(v))) || i18n.t('trivia.create.validation.resultsDisplay.required'),
          v => Number(v) >= 0 || i18n.t('trivia.create.validation.resultsDisplay.greaterOrEqual')
        ]
      },
      originalQuestions: [],
      radioTouched: false,
      editingQuestionIndex: null,
      maxQuestions: MAX_QUESTIONS,
      newQuestion: {
        title: '',
        type: 'SINGLE_CHOICE',
        options: [
          { label: '', isCorrect: false },
          { label: '', isCorrect: false }
        ],
        correctIndex: null,
        correctAnswer: true
      },
      triviaForm: {
        title: "",
        description: "",
        defaultTimeLimitSeconds: 20,
        defaultBasePoints: 1000,
        resultsDisplaySeconds: 5,
        speedBonusEnabled: true,
        autoAdvanceEnabled: true,
        questions: []
      }
    };
  },

  mounted() {
    const id = this.$route.query.id;
    if (id) {
      this.loadTrivia(id);
    }
  },

  beforeDestroy() {
    this.loading = false;
    this.saving = false;
  },

  computed: {
    isEditMode() {
      return this.$route.query.mode === 'edit';
    },
    isViewMode() {
      return this.$route.query.mode === 'view';
    },
    isEditingQuestion() {
      return this.editingQuestionIndex !== null;
    },
    questionFormTitle() {
      return this.isEditingQuestion ? this.$t('trivia.create.questionForm.edit') : this.$t('trivia.create.questionForm.new');
    },
    questionButtonLabel() {
      return this.isEditingQuestion ? this.$t('trivia.create.button.saveQuestion') : this.$t('trivia.create.button.addQuestion');
    },
    generalDataValid() {
      const time = Number(this.triviaForm.defaultTimeLimitSeconds);
      const points = Number(this.triviaForm.defaultBasePoints);
      const display = Number(this.triviaForm.resultsDisplaySeconds);
      return !!this.triviaForm.title?.trim() &&
        !isNaN(time) && time > 0 &&
        !isNaN(points) && points > 0 &&
        !isNaN(display) && display >= 0;
    },
    isNewQuestionValid() {
      if (!this.isEditingQuestion && this.triviaForm.questions.length >= MAX_QUESTIONS) return false;
      if (!this.newQuestion.title?.trim() || this.newQuestion.title.length > 255) return false;
      if (this.newQuestion.type === 'SINGLE_CHOICE') {
        if (this.newQuestion.options.some(o => o.label.length > 255)) return false;
        const filled = this.newQuestion.options.filter(o => o.label.trim() !== '');
        if (filled.length < 2) return false;
        if (this.newQuestion.correctIndex === null || this.newQuestion.correctIndex === undefined || this.newQuestion.correctIndex < 0) return false;
        if (!this.newQuestion.options[this.newQuestion.correctIndex]?.label?.trim()) return false;
      } else if (this.newQuestion.type === 'TRUE_FALSE') {
        if (this.newQuestion.correctAnswer === undefined || this.newQuestion.correctAnswer === null) return false;
      }
      return true;
    },
    allQuestionsValid() {
      return this.triviaForm.questions.every(q => {
        if (!q.title?.trim() || q.title.length > 255) return false;
        if (q.type === 'SINGLE_CHOICE') {
          if (q.options.some(o => o.label.length > 255)) return false;
          const filled = q.options.filter(o => o.label.trim() !== '');
          if (filled.length < 2) return false;
          if (!q.options.some(o => o.isCorrect)) return false;
        } else if (q.type === 'TRUE_FALSE') {
          if (q.correctAnswer === undefined || q.correctAnswer === null) return false;
        }
        return true;
      });
    },
    questionsStepValid() {
      const hasPending = !!this.newQuestion.title?.trim();
      if (hasPending && !this.isNewQuestionValid) return false;
      return this.allQuestionsValid && (this.triviaForm.questions.length > 0 || hasPending);
    },
    canAdvance() {
      if (this.isViewMode) return true;
      if (this.currentStep === 0) return this.generalDataValid;
      if (this.currentStep === 1) return this.questionsStepValid;
      return true;
    },
    canSave() {
      return this.generalDataValid && this.questionsStepValid;
    },
    questionsLimitReached() {
      return this.triviaForm.questions.length >= MAX_QUESTIONS;
    },
    radioErrorMessages() {
      if (this.newQuestion.type === 'SINGLE_CHOICE' && this.radioTouched &&
          (this.newQuestion.correctIndex === null || this.newQuestion.correctIndex === undefined || this.newQuestion.correctIndex < 0)) {
        return [this.$t('trivia.create.validation.option.required')];
      }
      return [];
    }
  },

  methods: {
    optionLetter(index) {
      return String.fromCharCode(65 + index);
    },

    addNewOption() {
      if (this.newQuestion.options.length >= 4) return;
      this.newQuestion.options.push({ label: '', isCorrect: false });
    },

    removeNewOption(idx) {
      this.newQuestion.options.splice(idx, 1);
      if (this.newQuestion.correctIndex === idx) {
        this.newQuestion.correctIndex = null;
      } else if (this.newQuestion.correctIndex > idx) {
        this.newQuestion.correctIndex--;
      }
    },

    addQuestion() {
      if (!this.isNewQuestionValid) return;

      const isEdit = this.editingQuestionIndex !== null;
      const original = isEdit ? this.triviaForm.questions[this.editingQuestionIndex] : null;

      const question = {
        questionId: original?.questionId,
        title: this.newQuestion.title,
        type: this.newQuestion.type,
        options: [],
        basePoints: this.newQuestion.basePoints || this.triviaForm.defaultBasePoints,
        timeLimitSeconds: this.newQuestion.timeLimitSeconds || this.triviaForm.defaultTimeLimitSeconds,
        position: isEdit ? original.position : this.triviaForm.questions.length + 1
      };

      if (this.newQuestion.type === 'SINGLE_CHOICE') {
        let newCorrect = null;
        question.options = this.newQuestion.options.reduce((acc, o, i) => {
          if (o.label.trim() === '') return acc;
          if (i === this.newQuestion.correctIndex) newCorrect = acc.length;
          acc.push({ optionId: o.optionId, label: o.label, position: acc.length + 1, isCorrect: false });
          return acc;
        }, []);
        if (newCorrect !== null) {
          question.options[newCorrect].isCorrect = true;
        }
      } else {
        question.correctAnswer = this.newQuestion.correctAnswer;
        question.options = [
          { label: 'Verdadero', position: 1, isCorrect: this.newQuestion.correctAnswer === true },
          { label: 'Falso', position: 2, isCorrect: this.newQuestion.correctAnswer === false }
        ];
      }

      if (isEdit) {
        this.$set(this.triviaForm.questions, this.editingQuestionIndex, question);
      } else {
        this.triviaForm.questions.push(question);
      }
      this.resetNewQuestion();
    },

    editQuestion(idx) {
      const q = this.triviaForm.questions[idx];
      const correctOption = q.type === 'TRUE_FALSE' ? q.options?.find(o => o.isCorrect) : null;
      const derivedCorrectAnswer = correctOption ? correctOption.label === 'Verdadero' : true;
      this.editingQuestionIndex = idx;
      this.radioTouched = false;
      this.newQuestion = {
        title: q.title,
        type: q.type,
        basePoints: q.basePoints,
        timeLimitSeconds: q.timeLimitSeconds,
        options: q.type === 'SINGLE_CHOICE'
          ? q.options.map(o => ({ ...o }))
          : [
              { label: '', isCorrect: false },
              { label: '', isCorrect: false }
            ],
        correctIndex: q.type === 'SINGLE_CHOICE'
          ? q.options.findIndex(o => o.isCorrect)
          : null,
        correctAnswer: q.type === 'TRUE_FALSE' ? (q.correctAnswer ?? derivedCorrectAnswer) : true
      };
    },

    cancelEditQuestion() {
      this.resetNewQuestion();
    },

    resetNewQuestion() {
      this.radioTouched = false;
      this.editingQuestionIndex = null;
      this.newQuestion = {
        title: '',
        type: 'SINGLE_CHOICE',
        options: [
          { label: '', isCorrect: false },
          { label: '', isCorrect: false }
        ],
        correctIndex: null,
        correctAnswer: true
      };
    },

    removeQuestion(idx) {
      this.triviaForm.questions.splice(idx, 1);
      this.triviaForm.questions.forEach((q, i) => { q.position = i + 1; });
    },

    async loadTrivia(id) {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      this.loading = true;
      try {
        const resp = await TriviaService.getTriviaById(idGroup, idEmpresa, idUsuario, id);
        if (resp.data && resp.data.payload) {
          const payload = resp.data.payload;
          this.triviaForm = {
            triviaId: payload.triviaId || payload.id,
            title: payload.title,
            description: payload.description || '',
            defaultTimeLimitSeconds: payload.defaultTimeLimitSeconds,
            defaultBasePoints: payload.defaultBasePoints,
            resultsDisplaySeconds: payload.resultsDisplaySeconds,
            speedBonusEnabled: payload.speedBonusEnabled,
            autoAdvanceEnabled: payload.autoAdvanceEnabled,
            questions: (payload.questions || []).map(q => {
              if (q.type === 'TRUE_FALSE' && (q.correctAnswer === undefined || q.correctAnswer === null) && q.options) {
                const correct = q.options.find(o => o.isCorrect);
                return { ...q, correctAnswer: correct ? correct.label === 'Verdadero' : true };
              }
              return q;
            })
          };
          this.originalQuestions = JSON.parse(JSON.stringify(payload.questions || []));
          if (this.isViewMode) {
            this.currentStep = 2;
          }
        }
      } catch (err) {
        console.error('Error cargando trivia', err);
      } finally {
        this.loading = false;
      }
    },

    async persistTrivia() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      const payload = normalizeTriviaForBackend(this.triviaForm);
      let triviaId = this.triviaForm.triviaId || this.triviaForm.id;

      if (triviaId) {
        const resp = await TriviaService.putUpdateTrivia(idGroup, idEmpresa, idUsuario, triviaId, payload);
        if (resp.data && resp.data.payload) {
          triviaId = resp.data.payload.triviaId;
        }
      } else {
        const resp = await TriviaService.postCreateTrivia(idGroup, idEmpresa, idUsuario, payload);
        if (resp.data && resp.data.payload) {
          triviaId = resp.data.payload.triviaId;
          this.triviaForm.triviaId = triviaId;
        }
      }

      const currentQuestionIds = this.triviaForm.questions.map(q => q.questionId).filter(Boolean);

      if (this.originalQuestions && this.originalQuestions.length > 0) {
        for (const q of this.originalQuestions) {
          if (q.questionId && !currentQuestionIds.includes(q.questionId)) {
            await TriviaService.deleteQuestion(idGroup, idEmpresa, idUsuario, q.questionId);
          }
        }
      }

      for (const q of this.triviaForm.questions) {
        const qPayload = normalizeQuestionForBackend(q, this.triviaForm.defaultBasePoints, this.triviaForm.defaultTimeLimitSeconds);
        if (q.questionId) {
          await TriviaService.putUpdateQuestion(idGroup, idEmpresa, idUsuario, q.questionId, qPayload);
        } else {
          const resp = await TriviaService.postCreateQuestion(
            idGroup,
            idEmpresa,
            idUsuario,
            triviaId,
            qPayload
          );
          if (resp.data && resp.data.payload) {
            q.questionId = resp.data.payload.questionId || resp.data.payload.id;
          }
        }
      }

      if (this.triviaForm.questions.length > 1) {
        const finalQuestionIds = this.triviaForm.questions.map(q => q.questionId).filter(Boolean);
        await TriviaService.putReorderQuestions(idGroup, idEmpresa, idUsuario, triviaId, finalQuestionIds);
      }

      this.originalQuestions = JSON.parse(JSON.stringify(this.triviaForm.questions));
      return triviaId;
    },

    addPendingQuestionIfAny() {
      if (this.isNewQuestionValid) {
        this.addQuestion();
        return true;
      }
      return !this.newQuestion.title?.trim();
    },

    async saveTrivia() {
      if (this.currentStep === 1) this.addPendingQuestionIfAny();
      if (!this.canSave) return;

      this.saving = true;
      try {
        await this.persistTrivia();
        this.$router.push({ name: 'trivia' });
      } catch (err) {
        console.error('Error guardando trivia', err.response?.data?.payload || err);
        Swal.fire({
          icon: 'error',
          title: i18n.t('trivia.create.error.save'),
          confirmButtonColor: '#6E17A5'
        });
      } finally {
        this.saving = false;
      }
    },

    async publishTrivia() {
      if (this.currentStep === 1) this.addPendingQuestionIfAny();
      if (!this.canSave) return;

      this.saving = true;
      try {
        const triviaId = await this.persistTrivia();
        const { idGroup, idEmpresa, idUsuario } = getSessionContext();
        await TriviaService.patchTriviaStatus(idGroup, idEmpresa, idUsuario, triviaId, 'PUBLISHED');
        this.$router.push({ name: 'trivia' });
      } catch (err) {
        console.error('Error publicando trivia', err.response?.data?.payload || err);
        Swal.fire({
          icon: 'error',
          title: i18n.t('trivia.create.error.publish'),
          confirmButtonColor: '#6E17A5'
        });
      } finally {
        this.saving = false;
      }
    },

    nextStep() {
      if (this.currentStep === 1) this.addPendingQuestionIfAny();
      if (this.currentStep < this.steps.length - 1) this.currentStep++;
    },

    previousStep() {
      if (this.currentStep > 0) this.currentStep--;
    }
  }
};
</script>

<style lang="css" scoped>
.trivia-wizard {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin: 20px;
}

.trivia-wizard__header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.trivia-wizard__header h1 {
  color: #6E17A5;
  font-family: "Amsi Pro", sans-serif;
  font-weight: normal;
}

.trivia-wizard__steps {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.trivia-wizard__step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background-color: #f0f0f0;
  color: #888;
}

.trivia-wizard__step--active {
  background-color: rgba(110, 23, 165, 0.15);
  color: #6E17A5;
  font-weight: 600;
}

.trivia-wizard__step--completed {
  background-color: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.trivia-wizard__step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: currentColor;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.trivia-wizard__content {
  flex: 1;
}

.trivia-wizard__footer {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
</style>
